/**
 * What a DMP section save from the panel stores (docgen reasons, round 5).
 *
 * Two rules, so that the panel and the server give ONE answer to "is this
 * document done?":
 *   1. Nothing is saved unless the user changed the text. The editor can hand
 *      back the stored HTML re-serialised (attribute order, a dropped class)
 *      without anyone typing; a failed section's notice saved that way would
 *      have become "generated" content.
 *   2. A failed section's notice is never user content. While the notice is
 *      still in the text, the section stays generation_failed; once the user
 *      has replaced it, the section is edited.
 */

/** The marks of the notice full generation stores (functions/src/dmp/dmpGenerator.ts failedSectionHtml). */
const NOTICE_CLASS = 'dmp-generation-failed';
const NOTICE_SENTENCE = 'This section could not be written automatically.';

const FAILED = new Set(['generation_failed', 'failed']);

const textOf = (html: string) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export function containsFailureNotice(html: string): boolean {
  const h = String(html ?? '');
  return h.includes(NOTICE_CLASS) || textOf(h).includes(NOTICE_SENTENCE);
}

export type DmpSaveDecision =
  | { save: false }
  | { save: true; status: 'edited' | 'generated' | 'generation_failed'; userEdited: boolean };

export function decideDmpSectionSave(args: {
  html: string;
  userChanged: boolean;
  stored: { content?: string; autoGeneratedContent?: string; status?: string };
}): DmpSaveDecision {
  const { html, userChanged, stored } = args;
  if (!userChanged) return { save: false };
  if (html === stored.content) return { save: false };
  const storedFailed = FAILED.has(String(stored.status ?? ''));
  // A failed section whose text is unchanged, apart from markup the editor
  // normalised, is not saved.
  if (storedFailed && textOf(html) === textOf(stored.content ?? '')) return { save: false };
  if (containsFailureNotice(html)) return { save: true, status: 'generation_failed', userEdited: true };
  const userEdited = html !== stored.autoGeneratedContent;
  return { save: true, status: userEdited ? 'edited' : 'generated', userEdited };
}
